import React from 'react';
import { LinkedLogo, NamedLink } from '../../components';
import { HELPLINE_DISPLAY, HELPLINE_TEL } from '../LandingPage/CtSections/ctConfig';

import css from './CtFooter.module.css';

// Container Trader footer. Links only to pages that are in the new site style,
// plus Terms of Service and Privacy Policy. Slogan, copyright and social links come from Console.
const SUPPORT_EMAIL = 'admin@thecontainertrader.com';

const cms = pageId => ({ name: 'CMSPage', params: { pageId } });
const COLUMNS = [
  {
    title: 'Buy',
    links: [
      { label: 'Browse containers', name: 'SearchPage' },
      { label: 'Buy It Now Deals', ...cms('container-trader-deals') },
      { label: 'Search by state', ...cms('location') },
      { label: 'Verified sellers', ...cms('verified-dealers') },
      { label: 'Delivery checklist', name: 'DeliveryChecklistPage' },
    ],
  },
  {
    title: 'Sell & haul',
    links: [
      { label: 'Sell on Container Trader', ...cms('seller') },
      { label: 'Become a dealer', ...cms('become-a-dealer') },
      { label: 'Dealer referral program', ...cms('referral-program') },
      { label: 'Transport companies', ...cms('transport_companies') },
      { label: 'Load board', name: 'LoadBoardPage' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About us', ...cms('about') },
      { label: 'Terms of Service', name: 'TermsOfServicePage' },
      { label: 'Privacy Policy', name: 'PrivacyPolicyPage' },
    ],
  },
];

const SocialIcon = ({ platform }) => {
  if (platform === 'facebook') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M13.5 22v-8h2.7l.4-3.2h-3.1V8.8c0-.9.3-1.5 1.6-1.5h1.7V4.4c-.3 0-1.3-.1-2.5-.1-2.4 0-4.1 1.5-4.1 4.2v2.3H7.5V14h2.7v8h3.3z" />
      </svg>
    );
  }
  if (platform === 'youtube') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M21.6 7.2a2.5 2.5 0 00-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.8.4A2.5 2.5 0 002.4 7.2 26 26 0 002 12a26 26 0 00.4 4.8 2.5 2.5 0 001.8 1.8C5.8 19 12 19 12 19s6.2 0 7.8-.4a2.5 2.5 0 001.8-1.8A26 26 0 0022 12a26 26 0 00-.4-4.8zM10 15V9l5.2 3L10 15z" />
      </svg>
    );
  }
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
};

const PLATFORM_LABEL = {
  facebook: 'Facebook',
  youtube: 'YouTube',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  twitter: 'X',
  tiktok: 'TikTok',
  pinterest: 'Pinterest',
};

// Container Trader: the copyright line names the legal company and keeps the year current,
// instead of using the Console footer text.
export const COMPANY_LEGAL_NAME = 'Container Trader LLC';
export const copyrightLine = (year = new Date().getFullYear()) =>
  `© ${year} ${COMPANY_LEGAL_NAME}. All rights reserved.`;

const CtFooter = props => {
  const { footer = {}, linkLogoToExternalSite } = props;
  const slogan = footer?.slogan?.content;
  const socials = (footer?.socialMediaLinks || [])
    .map(s => s?.link)
    .filter(l => l?.url && l?.platform);

  return (
    <footer id="footer" className={css.root}>
      <div className={css.inner}>
        <div className={css.top}>
          <div className={css.brand}>
            <LinkedLogo
              rootClassName={css.logoLink}
              logoClassName={css.logoWrapper}
              logoImageClassName={css.logoImage}
              linkToExternalSite={linkLogoToExternalSite}
              layout="desktop"
            />
            {slogan ? <p className={css.slogan}>{slogan}</p> : null}
            <div className={css.contact}>
              <span className={css.contactLabel}>24/7 automated helpline</span>
              <a href={`tel:${HELPLINE_TEL}`} className={css.phone}>
                {HELPLINE_DISPLAY}
              </a>
              <a href={`mailto:${SUPPORT_EMAIL}`} className={css.email}>
                {SUPPORT_EMAIL}
              </a>
            </div>
            {socials.length ? (
              <div className={css.socials}>
                {socials.map(s => (
                  <a
                    key={s.platform}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={css.social}
                    aria-label={`Container Trader on ${PLATFORM_LABEL[s.platform] || s.platform}`}
                  >
                    <SocialIcon platform={s.platform} />
                  </a>
                ))}
              </div>
            ) : null}
          </div>

          <nav className={css.columns} aria-label="Footer">
            {COLUMNS.map(col => (
              <div key={col.title} className={css.column}>
                <h2 className={css.colTitle}>{col.title}</h2>
                <ul className={css.list}>
                  {col.links.map(l => (
                    <li key={l.label}>
                      <NamedLink name={l.name} params={l.params} className={css.link}>
                        {l.label}
                      </NamedLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>
        <p className={css.copyright}>{copyrightLine()}</p>
      </div>
    </footer>
  );
};

export default CtFooter;
